import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Search,
  Plus,
  Receipt,
  Edit3,
  Trash2,
  X,
  Save,
  AlertTriangle,
  DollarSign,
  Calendar,
  RotateCcw,
  Download,
  Printer,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import api from '../config/api';
import DateFilter from '../components/DateFilter';
import SearchableSelect from '../components/SearchableSelect';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import PharmacyLogo from '../components/PharmacyLogo';

const defaultTypeColors = {
  Rent: 'bg-blue-100 text-blue-700',
  Utilities: 'bg-purple-100 text-purple-700',
  Salaries: 'bg-emerald-100 text-emerald-700',
  Transport: 'bg-amber-100 text-amber-700',
  Marketing: 'bg-pink-100 text-pink-700',
  Maintenance: 'bg-orange-100 text-orange-700',
  Insurance: 'bg-cyan-100 text-cyan-700',
  Other: 'bg-gray-100 text-gray-700',
};

const typeColorMap = {
  blue: 'bg-blue-100 text-blue-700',
  purple: 'bg-purple-100 text-purple-700',
  emerald: 'bg-emerald-100 text-emerald-700',
  amber: 'bg-amber-100 text-amber-700',
  pink: 'bg-pink-100 text-pink-700',
  orange: 'bg-orange-100 text-orange-700',
  cyan: 'bg-cyan-100 text-cyan-700',
  red: 'bg-red-100 text-red-700',
  gray: 'bg-gray-100 text-gray-700',
  green: 'bg-green-100 text-green-700',
  yellow: 'bg-yellow-100 text-yellow-700',
  indigo: 'bg-indigo-100 text-indigo-700',
};

function getBadgeClass(colorKey) {
  if (!colorKey) return 'bg-gray-100 text-gray-700';
  if (typeColorMap[colorKey]) return typeColorMap[colorKey];
  if (colorKey.startsWith('bg-')) return colorKey;
  return 'bg-gray-100 text-gray-700';
}

export default function Expenses() {
  const { user } = useAuth();
  const { settings } = useSettings?.() || {};
  const [expenses, setExpenses] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [editExp, setEditExp] = useState(null);
  const [deleteExp, setDeleteExp] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [showExportPDF, setShowExportPDF] = useState(false);
  const [addForm, setAddForm] = useState({ expense_type_id: '', category: '', description: '', amount: '', date: new Date().toISOString().split('T')[0] });
  const [newTypeFor, setNewTypeFor] = useState(null);
  const [newTypeName, setNewTypeName] = useState('');
  const [creatingType, setCreatingType] = useState(false);

  useEffect(() => {
    loadExpenseTypes();
    loadExpenses();
  }, []);

  const loadExpenseTypes = async () => {
    try {
      const res = await api.get('/expense-types');
      const list = Array.isArray(res) ? res : (res?.expense_types || []);
      setExpenseTypes(list.filter((t) => t.is_active !== 0 && t.is_active !== false));
    } catch (err) {
      console.error('Failed to load expense types:', err);
    }
  };

  const loadExpenses = async () => {
    try {
      let url = '/expenses?limit=500';
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      const res = await api.get(url);
      const list = Array.isArray(res) ? res : (res?.expenses || []);
      setExpenses(list);
    } catch (err) {
      toast.error(err.message || 'Failed to load expenses');
    }
  };

  const typeOptions = expenseTypes.map((t) => ({
    value: String(t.id),
    label: t.name,
    sublabel: null,
  }));

  const typeMap = {};
  expenseTypes.forEach((t) => { typeMap[t.id] = t; });

  const filtered = expenses.filter(
    (e) =>
      (e.category || '').toLowerCase().includes(search.toLowerCase()) ||
      (e.expense_type_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (e.description && e.description.toLowerCase().includes(search.toLowerCase()))
  );

  const resetFilters = () => {
    setSearch('');
    setDateFrom('');
    setDateTo('');
  };

  const applyDateFilter = () => {
    loadExpenses();
  };

  const totalExpenses = filtered.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
  const pharmacyName = settings?.pharmacy_name || 'Gaza Pharmacy';

  const categoryTotals = filtered.reduce((acc, exp) => {
    const cat = exp.expense_type_name || exp.category || 'Uncategorized';
    acc[cat] = (acc[cat] || 0) + (parseFloat(exp.amount) || 0);
    return acc;
  }, {});

  const getExpenseBadge = (exp) => {
    if (exp.expense_type_name) {
      const colorClass = getBadgeClass(exp.expense_type_color);
      return { label: exp.expense_type_name, colorClass };
    }
    return { label: exp.category || '-', colorClass: defaultTypeColors[exp.category] || 'bg-gray-100 text-gray-700' };
  };

  const openAdd = () => {
    setAddForm({ expense_type_id: '', category: '', description: '', amount: '', date: new Date().toISOString().split('T')[0] });
    setNewTypeFor(null);
    setShowAdd(true);
  };

  const createExpenseType = async (name) => {
    const trimmedName = name.trim();
    const existingType = expenseTypes.find(
      (type) => type.name.toLowerCase() === trimmedName.toLowerCase()
    );
    if (existingType) return existingType;

    const created = await api.post('/expense-types', {
      name: trimmedName,
      color: 'gray',
      is_active: 1,
      sort_order: expenseTypes.length + 1,
    });
    const newType = {
      id: created?.id,
      name: created?.name || trimmedName,
      color: created?.color || 'gray',
      is_active: created?.is_active ?? 1,
      sort_order: created?.sort_order ?? expenseTypes.length + 1,
    };

    setExpenseTypes((types) => [...types, newType]);
    toast.success(`Expense type “${newType.name}” added`);
    return newType;
  };

  const startNewType = (form) => {
    setNewTypeFor(form);
    setNewTypeName('');
  };

  const saveNewType = async () => {
    if (!newTypeName.trim()) {
      toast.error('Enter an expense type name');
      return;
    }

    setCreatingType(true);
    try {
      const newType = await createExpenseType(newTypeName);
      const typeValues = { expense_type_id: String(newType.id), category: newType.name };
      if (newTypeFor === 'add') {
        setAddForm((form) => ({ ...form, ...typeValues }));
      } else {
        setEditForm((form) => ({ ...form, ...typeValues }));
      }
      setNewTypeFor(null);
      setNewTypeName('');
    } catch (err) {
      toast.error(err.message || 'Failed to add expense type');
    } finally {
      setCreatingType(false);
    }
  };

  const handleAddTypeChange = async (val) => {
    if (val === '') {
      setAddForm({ ...addForm, expense_type_id: '', category: '' });
      return;
    }
    const matched = expenseTypes.find((t) => String(t.id) === val);
    if (matched) {
      setAddForm({ ...addForm, expense_type_id: String(matched.id), category: matched.name });
    } else {
      try {
        const newType = await createExpenseType(val);
        setAddForm({ ...addForm, expense_type_id: String(newType.id), category: newType.name });
      } catch (err) {
        toast.error(err.message || 'Failed to add expense type');
      }
    }
  };

  const handleEditTypeChange = async (val) => {
    if (val === '') {
      setEditForm({ ...editForm, expense_type_id: '', category: '' });
      return;
    }
    const matched = expenseTypes.find((t) => String(t.id) === val);
    if (matched) {
      setEditForm({ ...editForm, expense_type_id: String(matched.id), category: matched.name });
    } else {
      try {
        const newType = await createExpenseType(val);
        setEditForm({ ...editForm, expense_type_id: String(newType.id), category: newType.name });
      } catch (err) {
        toast.error(err.message || 'Failed to add expense type');
      }
    }
  };

  const saveNew = async (e) => {
    e.preventDefault();
    if (!addForm.category || !addForm.amount) return;
    try {
      await api.post('/expenses', {
        expense_type_id: addForm.expense_type_id ? parseInt(addForm.expense_type_id) : null,
        category: addForm.category,
        description: addForm.description,
        amount: parseFloat(addForm.amount) || 0,
        date: addForm.date,
        user_id: user?.id || 1,
      });
      toast.success('Expense added');
      setShowAdd(false);
      loadExpenses();
    } catch (err) {
      toast.error(err.message || 'Failed to add expense');
    }
  };

  const openEdit = (exp) => {
    setNewTypeFor(null);
    setEditExp(exp);
    setEditForm({
      ...exp,
      expense_type_id: exp.expense_type_id ? String(exp.expense_type_id) : '',
      date: exp.date ? new Date(exp.date).toISOString().split('T')[0] : '',
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/expenses/${editForm.id}`, {
        expense_type_id: editForm.expense_type_id ? parseInt(editForm.expense_type_id) : null,
        category: editForm.category,
        description: editForm.description,
        amount: parseFloat(editForm.amount) || 0,
        date: editForm.date,
      });
      toast.success('Expense updated');
      setEditExp(null);
      loadExpenses();
    } catch (err) {
      toast.error(err.message || 'Failed to update expense');
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/expenses/${deleteExp.id}`);
      toast.success('Expense deleted');
      setDeleteExp(null);
      loadExpenses();
    } catch (err) {
      toast.error(err.message || 'Failed to delete expense');
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-100">
            <DollarSign className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Total Expenses</p>
            <p className="text-2xl font-bold text-gray-900">Rs. {(parseFloat(totalExpenses) || 0).toFixed(2)}</p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search expenses..."
            className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-24 text-sm text-gray-900 placeholder-gray-400 transition-colors focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
          />
          <button className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-md bg-gray-100 px-4 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-200">Find</button>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowExportPDF(true)}
            disabled={filtered.length === 0}
            className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition-all hover:bg-gray-50 active:scale-[0.97] disabled:opacity-50"
            title="Export / Print Expenses as PDF"
          >
            <Download className="h-4 w-4 text-emerald-600" />
            <span>Export PDF</span>
          </button>
          <button onClick={openAdd} className="flex items-center gap-2 rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-emerald-600 active:scale-[0.97]">
            <Plus className="h-4 w-4" />New Expense
          </button>
        </div>
      </div>

      <DateFilter
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onApply={applyDateFilter}
        onReset={resetFilters}
      />

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-emerald-500" />
            <h2 className="text-base font-semibold text-gray-900">Expenses</h2>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/80">
                <th className="sticky-th-left px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Expense Type</th>
                <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Expense Details</th>
                <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Date</th>
                <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Amount</th>
                <th className="sticky-th-right px-6 py-3 whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((exp) => {
                const badge = getExpenseBadge(exp);
                return (
                  <tr key={exp.id} className="group transition-colors hover:bg-gray-50/50">
                    <td className="sticky-col-left px-6 py-4 whitespace-nowrap"><span className={clsx('inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold', badge.colorClass)}>{badge.label}</span></td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{exp.description || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{exp.date || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-red-600">Rs. {(parseFloat(exp.amount) || 0).toFixed(2)}</td>
                    <td className="sticky-col-right px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button onClick={() => openEdit(exp)} className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-amber-50 hover:text-amber-600"><Edit3 className="h-4 w-4" /></button>
                        <button onClick={() => setDeleteExp(exp)} className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <Receipt className="mb-3 h-12 w-12 text-gray-300" />
            <p className="text-sm">No expenses found</p>
          </div>
        )}
      </div>

      {/* ADD MODAL */}
      {showAdd &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowAdd(false)} />
            <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">Add Expense</h3>
                <button onClick={() => setShowAdd(false)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
              </div>
              <form onSubmit={saveNew} className="px-6 py-5">
                <div className="space-y-4">
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Expense Type *</label>
                    <SearchableSelect
                      value={addForm.expense_type_id || addForm.category}
                      onChange={handleAddTypeChange}
                      options={typeOptions.length > 0 ? typeOptions : undefined}
                      placeholder="Select expense type..."
                      searchPlaceholder="Search or create expense type..."
                      creatable
                      required
                    />
                    {newTypeFor === 'add' ? (
                      <div className="mt-2 flex gap-2">
                        <input
                          type="text"
                          value={newTypeName}
                          onChange={(e) => setNewTypeName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveNewType(); } }}
                          placeholder="New expense type name"
                          className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                          autoFocus
                        />
                        <button type="button" onClick={saveNewType} disabled={creatingType} className="rounded-lg bg-emerald-500 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-600 disabled:opacity-60">
                          {creatingType ? 'Adding...' : 'Add'}
                        </button>
                        <button type="button" onClick={() => setNewTypeFor(null)} className="rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => startNewType('add')} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:text-emerald-700">
                        <Plus className="h-3.5 w-3.5" /> New type
                      </button>
                    )}
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Expense Details</label>
                    <textarea value={addForm.description} onChange={(e) => setAddForm({ ...addForm, description: e.target.value })} rows={2} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none resize-none" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Amount (Rs.) *</label>
                      <input type="number" step="0.01" value={addForm.amount} onChange={(e) => setAddForm({ ...addForm, amount: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" required />
                    </div>
                    <div>
                      <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Date</label>
                      <input type="date" value={addForm.date} onChange={(e) => setAddForm({ ...addForm, date: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
                    </div>
                  </div>
                </div>
                <div className="mt-5 flex justify-end gap-3 border-t border-gray-100 pt-5">
                  <button type="button" onClick={() => setShowAdd(false)} className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                  <button type="submit" className="flex items-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600"><Save className="h-4 w-4" />Save Expense</button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* EDIT MODAL */}
      {editExp &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setEditExp(null)} />
            <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">Edit Expense</h3>
                <button onClick={() => setEditExp(null)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
              </div>
              <form onSubmit={saveEdit} className="px-6 py-5">
                <div className="space-y-4">
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Expense Type</label>
                    <SearchableSelect
                      value={editForm.expense_type_id || editForm.category}
                      onChange={handleEditTypeChange}
                      options={typeOptions.length > 0 ? typeOptions : undefined}
                      placeholder="Select expense type..."
                      searchPlaceholder="Search or create expense type..."
                      creatable
                    />
                    {newTypeFor === 'edit' ? (
                      <div className="mt-2 flex gap-2">
                        <input
                          type="text"
                          value={newTypeName}
                          onChange={(e) => setNewTypeName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveNewType(); } }}
                          placeholder="New expense type name"
                          className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                          autoFocus
                        />
                        <button type="button" onClick={saveNewType} disabled={creatingType} className="rounded-lg bg-emerald-500 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-600 disabled:opacity-60">
                          {creatingType ? 'Adding...' : 'Add'}
                        </button>
                        <button type="button" onClick={() => setNewTypeFor(null)} className="rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => startNewType('edit')} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:text-emerald-700">
                        <Plus className="h-3.5 w-3.5" /> New type
                      </button>
                    )}
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Expense Details</label>
                    <textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} rows={2} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none resize-none" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Amount</label>
                      <input type="number" step="0.01" value={editForm.amount} onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
                    </div>
                    <div>
                      <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Date</label>
                      <input type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
                    </div>
                  </div>
                </div>
                <div className="mt-5 flex justify-end gap-3 border-t border-gray-100 pt-5">
                  <button type="button" onClick={() => setEditExp(null)} className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                  <button type="submit" className="flex items-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600"><Save className="h-4 w-4" />Save Changes</button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* DELETE CONFIRM MODAL */}
      {deleteExp &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setDeleteExp(null)} />
            <div className="relative w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-7 w-7 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Expense?</h3>
              <p className="mt-2 text-sm text-gray-500">Are you sure you want to delete this expense?</p>
              <div className="mt-6 flex justify-center gap-3">
                <button onClick={() => setDeleteExp(null)} className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                <button onClick={confirmDelete} className="rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700">Delete</button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* EXPENSES PDF EXPORT PREVIEW MODAL */}
      {showExportPDF &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
            <div className="fixed inset-0" onClick={() => setShowExportPDF(false)} />
            <div className="relative w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200">
              <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50/80 px-6 py-4 flex-shrink-0">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                    <Download className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Expenses Report Preview</h3>
                    <p className="text-[10px] text-gray-500">{filtered.length} entries | Generated {new Date().toLocaleString()}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowExportPDF(false)}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-200/70 hover:text-gray-700 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 bg-gray-100 flex justify-center">
                <div id="printable-expenses-report" className="w-full max-w-[750px] bg-white shadow-md border border-gray-200 rounded-lg p-8">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, borderBottom: '3px solid #22c55e', paddingBottom: 16 }}>
                    <div>
                      <PharmacyLogo logo={settings?.pharmacy_logo} alt="Shop logo" className="h-10 w-10 rounded-lg mb-2" imageClassName="h-10 w-10 rounded-lg object-contain mb-2" />
                      <h1 style={{ fontSize: 22, color: '#22c55e', margin: 0, fontWeight: 700 }}>{pharmacyName}</h1>
                      <p style={{ fontSize: 13, color: '#4b5563', margin: '2px 0 0', fontWeight: 600 }}>Expenses Report</p>
                      {settings?.pharmacy_address && (
                        <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0' }}>{settings.pharmacy_address}</p>
                      )}
                      {settings?.pharmacy_phone && (
                        <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0' }}>Tel: {settings.pharmacy_phone}</p>
                      )}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ fontSize: 11, color: '#6b7280', margin: 0 }}>Generated: {new Date().toLocaleString()}</p>
                      <p style={{ fontSize: 11, color: '#6b7280', margin: '3px 0 0' }}>Records: {filtered.length} entries</p>
                      {(dateFrom || dateTo) && (
                        <p style={{ fontSize: 11, color: '#059669', fontWeight: 600, margin: '3px 0 0' }}>
                          Period: {dateFrom || 'Start'} to {dateTo || 'End'}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Summary Cards */}
                  <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                    <div style={{ flex: 1, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                      <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Total Expenses</p>
                      <p style={{ fontSize: 18, fontWeight: 700, color: '#dc2626', marginTop: 4, marginBottom: 0 }}>Rs. {totalExpenses.toFixed(2)}</p>
                    </div>
                    <div style={{ flex: 1, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                      <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Total Entries</p>
                      <p style={{ fontSize: 18, fontWeight: 700, color: '#1f2937', marginTop: 4, marginBottom: 0 }}>{filtered.length}</p>
                    </div>
                    <div style={{ flex: 1, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                      <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Categories</p>
                      <p style={{ fontSize: 18, fontWeight: 700, color: '#16a34a', marginTop: 4, marginBottom: 0 }}>{Object.keys(categoryTotals).length}</p>
                    </div>
                  </div>

                  {/* Category Breakdown */}
                  {Object.keys(categoryTotals).length > 1 && (
                    <div style={{ marginBottom: 20 }}>
                      <h4 style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#4b5563', marginBottom: 8 }}>Category Breakdown</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
                        {Object.entries(categoryTotals).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => (
                          <div key={cat} style={{ padding: '8px 10px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 11, color: '#4b5563', fontWeight: 500 }}>{cat}</span>
                            <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 700 }}>Rs. {amt.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Detailed Expenses Table */}
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                      <tr>
                        <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>#</th>
                        <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>Date</th>
                        <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>Expense Type</th>
                        <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>Details / Note</th>
                        <th style={{ background: '#22c55e', color: 'white', textAlign: 'right', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((exp, i) => (
                        <tr key={exp.id || i} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                          <td style={{ padding: '7px 10px', color: '#6b7280', fontSize: 10 }}>{i + 1}</td>
                          <td style={{ padding: '7px 10px', color: '#374151' }}>{exp.date || '-'}</td>
                          <td style={{ padding: '7px 10px', fontWeight: 600, color: '#111827' }}>{exp.expense_type_name || exp.category || '-'}</td>
                          <td style={{ padding: '7px 10px', color: '#4b5563' }}>{exp.description || '-'}</td>
                          <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>Rs. {(parseFloat(exp.amount) || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                      <tr style={{ borderTop: '2px solid #22c55e', background: '#f0fdf4' }}>
                        <td colSpan={4} style={{ padding: '9px 10px', fontWeight: 700, textAlign: 'right', color: '#166534', textTransform: 'uppercase', fontSize: 10 }}>Total Amount:</td>
                        <td style={{ padding: '9px 10px', textAlign: 'right', fontWeight: 800, color: '#dc2626', fontSize: 12 }}>Rs. {totalExpenses.toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>

                  <div style={{ marginTop: 24, textAlign: 'center', fontSize: 9, color: '#9ca3af', borderTop: '1px solid #e5e7eb', paddingTop: 12, lineHeight: 1.6 }}>
                    <div>{pharmacyName} Management System — Printed on {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
                    <div style={{ marginTop: 2, color: '#6b7280' }}>
                      Developed by{' '}
                      <a
                        href="https://jahasoft.pk"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#059669', textDecoration: 'none', fontWeight: 600 }}
                      >
                        Jahasoft (https://jahasoft.pk)
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 border-t border-gray-100 bg-white p-4 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setShowExportPDF(false)}
                  className="rounded-xl border border-gray-200 px-4 py-2.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-2.5 text-xs font-bold text-white shadow-md shadow-emerald-600/25 hover:bg-emerald-700 active:scale-98 transition-all"
                >
                  <Printer className="h-4 w-4" />
                  Print / Save as PDF
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* DEDICATED PRINT CONTAINER FOR EXPENSES EXPORT */}
      {showExportPDF &&
        createPortal(
          <div id="printable-expenses-export" className="hidden print:block" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', padding: 30, color: '#1a1a1a' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, borderBottom: '3px solid #22c55e', paddingBottom: 16 }}>
              <div>
                <PharmacyLogo logo={settings?.pharmacy_logo} alt="Shop logo" className="h-10 w-10 rounded-lg mb-2" imageClassName="h-10 w-10 rounded-lg object-contain mb-2" />
                <h1 style={{ fontSize: 22, color: '#22c55e', margin: 0, fontWeight: 700 }}>{pharmacyName}</h1>
                <p style={{ fontSize: 13, color: '#4b5563', margin: '2px 0 0', fontWeight: 600 }}>Expenses Report</p>
                {settings?.pharmacy_address && (
                  <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0' }}>{settings.pharmacy_address}</p>
                )}
                {settings?.pharmacy_phone && (
                  <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0' }}>Tel: {settings.pharmacy_phone}</p>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontSize: 11, color: '#6b7280', margin: 0 }}>Generated: {new Date().toLocaleString()}</p>
                <p style={{ fontSize: 11, color: '#6b7280', margin: '3px 0 0' }}>Records: {filtered.length} entries</p>
                {(dateFrom || dateTo) && (
                  <p style={{ fontSize: 11, color: '#059669', fontWeight: 600, margin: '3px 0 0' }}>
                    Period: {dateFrom || 'Start'} to {dateTo || 'End'}
                  </p>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
              <div style={{ flex: 1, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Total Expenses</p>
                <p style={{ fontSize: 18, fontWeight: 700, color: '#dc2626', marginTop: 4, marginBottom: 0 }}>Rs. {totalExpenses.toFixed(2)}</p>
              </div>
              <div style={{ flex: 1, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Total Entries</p>
                <p style={{ fontSize: 18, fontWeight: 700, color: '#1f2937', marginTop: 4, marginBottom: 0 }}>{filtered.length}</p>
              </div>
              <div style={{ flex: 1, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Categories</p>
                <p style={{ fontSize: 18, fontWeight: 700, color: '#16a34a', marginTop: 4, marginBottom: 0 }}>{Object.keys(categoryTotals).length}</p>
              </div>
            </div>

            {Object.keys(categoryTotals).length > 1 && (
              <div style={{ marginBottom: 20 }}>
                <h4 style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#4b5563', marginBottom: 8 }}>Category Breakdown</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
                  {Object.entries(categoryTotals).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => (
                    <div key={cat} style={{ padding: '8px 10px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 11, color: '#4b5563', fontWeight: 500 }}>{cat}</span>
                      <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 700 }}>Rs. {amt.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>#</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>Date</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>Expense Type</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'left', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>Details / Note</th>
                  <th style={{ background: '#22c55e', color: 'white', textAlign: 'right', padding: '8px 10px', fontSize: 9, textTransform: 'uppercase' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((exp, i) => (
                  <tr key={exp.id || i} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                    <td style={{ padding: '7px 10px', color: '#6b7280', fontSize: 10 }}>{i + 1}</td>
                    <td style={{ padding: '7px 10px', color: '#374151' }}>{exp.date || '-'}</td>
                    <td style={{ padding: '7px 10px', fontWeight: 600, color: '#111827' }}>{exp.expense_type_name || exp.category || '-'}</td>
                    <td style={{ padding: '7px 10px', color: '#4b5563' }}>{exp.description || '-'}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>Rs. {(parseFloat(exp.amount) || 0).toFixed(2)}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid #22c55e', background: '#f0fdf4' }}>
                  <td colSpan={4} style={{ padding: '9px 10px', fontWeight: 700, textAlign: 'right', color: '#166534', textTransform: 'uppercase', fontSize: 10 }}>Total Amount:</td>
                  <td style={{ padding: '9px 10px', textAlign: 'right', fontWeight: 800, color: '#dc2626', fontSize: 12 }}>Rs. {totalExpenses.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>

            <div style={{ marginTop: 24, textAlign: 'center', fontSize: 9, color: '#9ca3af', borderTop: '1px solid #e5e7eb', paddingTop: 12, lineHeight: 1.6 }}>
              <div>{pharmacyName} Management System — Printed on {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
              <div style={{ marginTop: 2, color: '#6b7280' }}>
                Developed by{' '}
                <a
                  href="https://jahasoft.pk"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#059669', textDecoration: 'none', fontWeight: 600 }}
                >
                  Jahasoft (https://jahasoft.pk)
                </a>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
